import { useEffect, useMemo, useState } from "react";
import { Info, X } from "lucide-react";
import { useTheme } from "../../context/themeContext";

const defaultData = [
  { id: 0, value: 1, color: "#2A2C3A", label: "No data yet" },
];
const labelDescriptions = {
  "Innovator (Openness)":
    "Introspective, independent, and imaginative, much like the qualities associated with Openness. They are thinkers who value autonomy and original ideas.",
  "Achiever (Conscientiousness)":
    "Disciplined, organized, and often perfectionistic. They resonate with the Achiever's focus on responsibility, structure, and conscientious behavior.",
  "Connector (Extraversion) ":
    "Are often socially engaging and seek connection, closely mirroring the Connector's extroverted nature. They thrive on interpersonal relationships and expressive communication.",
  "Collaborator (Agreeableness)":
    "Are often prioritize others' needs and are cooperative and patient, similar to the Collaborator's empathy, cooperation, and focus on teamwork and harmony.",
  "Resilient (Emotional Stability)":
    "Demonstrate emotional resilience and adaptability, though not in a clinical sense. They can handle high-pressure situations with a level head, much like the Resilient personality type does.",
};
const RADIUS = 74;
const STROKE = 20;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const DonutChart = ({
  data,
  title,
  personalGrowth = false,
  centerCaption = "total",
}) => {
  const { isDark } = useTheme();
  const hasData = data && data.length > 0;
  const chartData = hasData ? data : [];
  const total = useMemo(
    () => chartData.reduce((acc, d) => acc + (d.value || 0), 0),
    [chartData],
  );
  const [mounted, setMounted] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, [chartData]);
  let cumulative = 0;
  const segments = chartData.map((d) => {
    const fraction = total > 0 ? d.value / total : 0;
    const dash = fraction * CIRCUMFERENCE;
    const offset = -cumulative * CIRCUMFERENCE;
    cumulative += fraction;
    return { ...d, fraction, dash, offset };
  });
  const hovered = segments.find((s) => s.id === hoveredId) || null;
  const trackColor = isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)";

  return (
    <div
      className="w-full flex flex-col items-center gap-4"
      role="img"
      aria-label={title}
    >
      {/* Fixed 200x200 box regardless of hasData, so the centered label
          below always centers against the same footprint the SVG would
          have used — otherwise, with no SVG rendered, this wrapper
          collapses to its padding and the absolutely-positioned text
          snaps to the top instead of the middle. */}
      <div className="relative flex items-center justify-center w-[200px] h-[200px]">
        {hasData && (
          <svg viewBox="0 0 200 200" className="w-[200px] h-[200px] -rotate-90">
            <circle
              cx="100"
              cy="100"
              r={RADIUS}
              fill="none"
              stroke={trackColor}
              strokeWidth={STROKE}
            />
            {segments.map((s) => {
              const isHovered = hoveredId === s.id;
              const isDimmed = hoveredId !== null && !isHovered;
              return (
                <circle
                  key={s.id}
                  cx="100"
                  cy="100"
                  r={RADIUS}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={isHovered ? STROKE + 4 : STROKE}
                  strokeLinecap="butt"
                  strokeDasharray={`${mounted ? s.dash : 0} ${CIRCUMFERENCE - (mounted ? s.dash : 0)}`}
                  strokeDashoffset={mounted ? s.offset : 0}
                  opacity={isDimmed ? 0.25 : 1}
                  style={{
                    transition:
                      "stroke-dasharray 900ms cubic-bezier(.22,1,.36,1), stroke-dashoffset 900ms cubic-bezier(.22,1,.36,1), stroke-width 200ms ease, opacity 200ms ease",
                    filter: isHovered
                      ? `drop-shadow(0 0 8px ${s.color}99)`
                      : "none",
                    cursor: "pointer",
                  }}
                  onMouseEnter={() => setHoveredId(s.id)}
                  onMouseLeave={() => setHoveredId(null)}
                />
              );
            })}
          </svg>
        )}

        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {hasData ? (
            <>
              <span className="font-['IBM_Plex_Mono',_monospace] text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
                {hovered
                  ? `${((hovered.fraction || 0) * 100).toFixed(1)}%`
                  : total.toLocaleString()}
              </span>
              <span className="w-20 text-[10px] text-center uppercase tracking-wider text-slate-400 dark:text-white/40 break-words whitespace-normal">
                {hovered ? hovered.label.trim() : centerCaption}
              </span>
            </>
          ) : (
            <span className="text-center text-sm text-slate-500 dark:text-white/50 px-1">
              None of your users discussed{" "}
              <span className="font-medium">{title}</span> yet.
            </span>
          )}
        </div>
      </div>

      <div className="w-full flex flex-wrap justify-center gap-x-4 gap-y-2">
        {[...segments]
          .filter((s) => s.fraction > 0)
          .sort((a, b) => b.fraction - a.fraction)
          .map((s) => {
            const description = personalGrowth
              ? labelDescriptions[s.label]
              : null;

            return (
              <button
                type="button"
                key={s.id}
                onMouseEnter={() => setHoveredId(s.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="group flex items-center gap-1.5 text-left"
                title={description || undefined}
              >
                <span
                  className="w-2.5 h-2.5 rounded-[3px] shrink-0 transition-transform duration-200 group-hover:scale-125"
                  style={{ backgroundColor: s.color }}
                />
                <span className="text-xs text-slate-500 dark:text-white/60 group-hover:text-slate-900 dark:group-hover:text-white transition-colors duration-150 whitespace-nowrap">
                  {s.label}
                </span>
                <span className="font-['IBM_Plex_Mono',_monospace] text-[11px] text-slate-400 dark:text-white/35 group-hover:text-slate-600 dark:group-hover:text-white/70 transition-colors duration-150">
                  {(s.fraction * 100).toFixed(0)}%
                </span>
              </button>
            );
          })}
      </div>

      {personalGrowth && (
        <>
          <button
            type="button"
            onClick={() => setShowInfo(true)}
            className="absolute bottom-3 right-3 p-1.5 rounded-full text-slate-400 dark:text-white/40 hover:text-slate-900 dark:hover:text-white
                       hover:bg-black/[0.06] dark:hover:bg-white/[0.08] transition-colors duration-200"
            aria-label="About this chart"
          >
            <Info size={16} />
          </button>

          {showInfo && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 backdrop-blur-sm p-4"
              onClick={() => setShowInfo(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-sm rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1B1D29]/95 p-5 shadow-2xl"
              >
                <button
                  onClick={() => setShowInfo(false)}
                  className="absolute top-3 right-3 text-slate-400 dark:text-white/40 hover:text-slate-900 dark:hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
                <p className="text-xs leading-relaxed text-slate-600 dark:text-white/60">
                  Disclaimer: These insights are based on the OCEAN framework
                  and are intended to provide general insights into employee
                  work styles. While they can be useful for understanding
                  tendencies, they are not definitive or clinical assessments
                  and should not be viewed as absolute. Use these insights as a
                  guide alongside individual feedback to support effective role
                  alignment and workplace well-being.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
var DonutChart_default = DonutChart;
export { DonutChart_default as default };